import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Check, Zap, ChevronDown, X as XIcon,
  Shield, Globe, Mail, Twitter, Github, Linkedin
} from 'lucide-react';

/* ─── Data ─── */

const features = [
  {
    title: 'AI Proposals',
    desc: 'Paste your meeting notes and our AI writes polished, professional proposals in seconds. Edit, preview, and send with one click.',
    bullets: ['Meeting-notes-to-proposal in seconds', 'Rich markdown editor', 'One-click client sharing'],
  },
  {
    title: 'Smart Invoicing',
    desc: 'Create invoices from projects, set tax rates, and send payment links. Clients pay online with Razorpay, so you get paid faster.',
    bullets: ['Create from projects instantly', 'Automatic tax calculation', 'Razorpay payment links'],
  },
  {
    title: 'Payment-Gated Delivery',
    desc: 'Upload deliverables and lock them behind payment. Files unlock automatically when your invoice is paid.',
    bullets: ['Upload any file type', 'Auto-unlock on payment', 'Client delivery portal'],
  },
  {
    title: 'Client Management',
    desc: 'Keep all your clients organized with contact info, project history, and communication logs in one place.',
    bullets: ['Centralized client profiles', 'Project & invoice history', 'Quick-access portal links'],
  },
  {
    title: 'Project Tracking',
    desc: 'Track project status, deadlines, and deliverables. Link proposals and invoices to projects for a complete view.',
    bullets: ['Status tracking & timelines', 'Link proposals & invoices', 'Time tracking built-in'],
  },
  {
    title: 'Analytics & Insights',
    desc: 'See your revenue trends, proposal win rates, top clients, and hours tracked, all in one dashboard.',
    bullets: ['Revenue & hours dashboards', 'Proposal win-rate tracking', 'Client revenue ranking'],
  },
];

const plans = [
  { name: 'Free', price: '$0', period: '/month', features: ['3 proposals/month', '3 invoices/month', '2 projects', '5 clients', '1 template'], cta: 'Get Started', popular: false },
  { name: 'Pro', price: '$20', period: '/month', features: ['50 proposals/month', '50 invoices/month', '25 projects', '50 clients', '10 templates', 'Priority support'], cta: 'Start Pro', popular: true },
  { name: 'Premium', price: '$45', period: '/month', features: ['Unlimited proposals', 'Unlimited invoices', 'Unlimited projects', 'Unlimited clients', 'Unlimited templates', 'Priority support', 'Custom branding'], cta: 'Start Premium', popular: false },
];

const faqs = [
  { q: 'How does the AI proposal generation work?', a: 'Simply paste your meeting notes or client brief, and our AI analyzes the content to generate a professional, structured proposal. You can edit, customize, and send it directly to your client, all within FlowLancer.' },
  { q: 'Is my data secure?', a: 'Absolutely. All data is encrypted at rest and in transit. We use industry-standard security practices including row-level security on our database. Your client data and files are never shared with third parties.' },
  { q: 'How does payment-gated delivery work?', a: 'Upload your deliverables (designs, code, documents, etc.) to a project. When you create an invoice and share the delivery link, the files remain locked until the client completes payment via Razorpay. Once paid, files unlock automatically.' },
  { q: 'Can I try FlowLancer for free?', a: 'Yes! Our Free plan includes 3 proposals, 3 invoices, 2 projects, and 5 clients per month, enough to experience the full platform. Upgrade anytime to unlock more capacity.' },
  { q: 'What payment methods do clients use?', a: 'Clients pay through Razorpay, which supports UPI, netbanking, credit and debit cards, and popular wallets. You connect your Razorpay account in Settings to receive payments directly.' },
  { q: 'Can I cancel or change my plan anytime?', a: 'Yes, you can upgrade, downgrade, or cancel your plan at any time from the Settings page. Changes take effect at the start of your next billing cycle.' },
];

const howItWorks = [
  { step: 1, title: 'Paste Meeting Notes', desc: 'Drop in your meeting notes, client brief, or project requirements. Our AI picks up every detail.' },
  { step: 2, title: 'AI Generates Proposal', desc: 'Within seconds, get a polished, professional proposal with scope, timeline, and pricing, ready to customize.' },
  { step: 3, title: 'Send & Get Paid', desc: 'Share the proposal, convert to invoice, and deliver files, all payment-gated. Get paid without the chase.' },
];

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

/* ─── Scroll-reveal hook ─── */
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, className = '', delay = 0 }) {
  const ref = useReveal();
  return (
    <div ref={ref} className={`reveal ${className}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined}>
      {children}
    </div>
  );
}

/* ─── FAQ Item ─── */
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-line">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 py-4 text-left transition-colors hover:text-forest"
        aria-expanded={open}
      >
        <span className="text-body text-ink">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-ink-subtle shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          strokeWidth={1.5}
        />
      </button>
      <div className={`faq-answer ${open ? 'open' : ''}`}>
        <p className="pb-5 pr-8 text-small text-ink-muted">{a}</p>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  const handleCTA = () => {
    if (user) navigate('/dashboard');
    else navigate('/login');
  };

  const scrollTo = (href) => {
    setMobileNav(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-paper">

      {/* ═══ NAV ═══ */}
      <nav className="sticky top-0 z-50 w-full border-b border-line bg-paper" data-testid="landing-nav">
        <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
            <span className="font-display text-subhead text-ink">FlowLancer</span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((l) => (
              <button key={l.href} onClick={() => scrollTo(l.href)} className="text-small text-ink-muted hover:text-forest transition-colors">
                {l.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} data-testid="go-to-dashboard">
                Dashboard
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn" className="hidden sm:inline-flex">Sign in</Button>
                <Button onClick={() => navigate('/login')} data-testid="signup-btn">
                  Start free
                </Button>
              </>
            )}
            {/* Mobile hamburger */}
            <button className="md:hidden p-1" onClick={() => setMobileNav(!mobileNav)} aria-label="Menu">
              {mobileNav ? <XIcon className="w-5 h-5 text-ink" strokeWidth={1.5} /> : (
                <svg className="w-5 h-5 text-ink" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {mobileNav && (
          <div className="md:hidden border-t border-line bg-paper px-6 py-4 space-y-3">
            {navLinks.map((l) => (
              <button key={l.href} onClick={() => scrollTo(l.href)} className="block w-full text-left text-small text-ink-muted hover:text-forest py-2">
                {l.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="mx-auto max-w-[1200px] px-6 pb-24 pt-20 md:pt-28" data-testid="hero-section">
        <div className="max-w-[820px]">
          <h1 className="font-display text-display-xl text-ink">
            Get paid like a studio.
          </h1>
          <p className="mt-6 max-w-[560px] text-body text-ink-muted">
            Proposals, invoices, and payment-gated delivery in one place.
            Send the work, get paid, skip the chase.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Button size="lg" onClick={handleCTA} data-testid="hero-cta">
              Start free
            </Button>
            <Button size="lg" variant="link" onClick={() => scrollTo('#how-it-works')}>
              See how it works
            </Button>
          </div>
          <p className="mt-6 text-small text-ink-subtle">
            Free plan: 3 proposals and 3 invoices a month. No card required.
          </p>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="mx-auto max-w-[1200px] px-6 py-20 md:py-28" data-testid="how-it-works-section">
        <Reveal>
          <h2 className="max-w-[640px] font-display text-display-l text-ink">
            From meeting notes to payment in three steps
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8">
          {howItWorks.map((step, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="border-t border-line pt-4">
                <p className="font-display text-metric text-ink-subtle tabular-nums">
                  {String(step.step).padStart(2, '0')}
                </p>
                <h3 className="mt-3 text-subhead text-ink">{step.title}</h3>
                <p className="mt-2 text-small text-ink-muted">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" className="mx-auto max-w-[1200px] px-6 py-20 md:py-28" data-testid="features-section">
        <Reveal>
          <h2 className="max-w-[640px] font-display text-display-l text-ink">
            Everything you need to run your freelance business
          </h2>
          <p className="mt-5 max-w-[560px] text-body text-ink-muted">
            From proposal to payment, FlowLancer handles the business side so you can focus on the work.
          </p>
        </Reveal>
        <div className="mt-14">
          {features.map((f, i) => (
            <Reveal key={i}>
              <div
                className="grid grid-cols-1 gap-4 border-t border-line py-8 md:grid-cols-12 md:gap-8"
                data-testid={`feature-card-${i}`}
              >
                <div className="md:col-span-4">
                  <p className="text-micro uppercase text-ink-subtle tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="mt-2 text-subhead text-ink">{f.title}</h3>
                </div>
                <div className="md:col-span-8">
                  <p className="text-body text-ink-muted">{f.desc}</p>
                  <ul className="mt-4 space-y-2">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2.5 text-small text-ink-muted">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" strokeWidth={1.5} />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="mx-auto max-w-[1200px] px-6 py-20 md:py-28" data-testid="pricing-section">
        <Reveal>
          <h2 className="font-display text-display-l text-ink">Simple, transparent pricing</h2>
          <p className="mt-5 text-body text-ink-muted">Start free, upgrade when you're ready.</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`flex flex-col pt-5 lg:px-8 lg:first:pl-0 lg:last:pr-0 ${
                plan.popular ? 'border-t-2 border-forest' : 'border-t border-line'
              } ${i > 0 ? 'mt-10 sm:mt-0 lg:border-l lg:border-l-line' : ''}`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="text-subhead text-ink">{plan.name}</h3>
                {plan.popular && (
                  <span className="text-micro uppercase text-forest">Most popular</span>
                )}
              </div>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="font-sans text-metric tabular-nums text-ink">{plan.price}</span>
                <span className="text-small text-ink-subtle">{plan.period}</span>
              </div>
              <ul className="mt-6 mb-8 flex-grow space-y-2.5">
                {plan.features.map((feat, j) => (
                  <li key={j} className="flex items-start gap-2.5 text-small text-ink-muted">
                    <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-forest" strokeWidth={1.5} />
                    {feat}
                  </li>
                ))}
              </ul>
              <Button
                onClick={handleCTA}
                variant={plan.popular ? 'default' : 'outline'}
                className="w-full"
              >
                {plan.cta}
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className="mx-auto max-w-[760px] px-6 py-20 md:py-28" data-testid="faq-section">
        <Reveal>
          <h2 className="font-display text-display-l text-ink">Frequently asked questions</h2>
        </Reveal>
        <div className="mt-12">
          {faqs.map((faq, i) => (
            <FAQItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>
      </section>

      {/* ═══ BOTTOM CTA ═══ */}
      <section className="mx-auto max-w-[1200px] px-6 py-20 md:py-28" data-testid="cta-section">
        <Reveal>
          <div className="border-t border-line pt-10">
            <h2 className="max-w-[640px] font-display text-display-l text-ink">
              Ready to get paid like a studio?
            </h2>
            <p className="mt-5 max-w-[520px] text-body text-ink-muted">
              Start on the free plan and upgrade only when the work outgrows it.
            </p>
            <div className="mt-8">
              <Button onClick={handleCTA} size="lg" data-testid="bottom-cta">
                Start free
              </Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-small text-ink-subtle">
              <span className="flex items-center gap-1.5"><Shield className="h-3.5 w-3.5" strokeWidth={1.5} /> SSL encrypted</span>
              <span className="flex items-center gap-1.5"><Zap className="h-3.5 w-3.5" strokeWidth={1.5} /> No credit card required</span>
              <span className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5" strokeWidth={1.5} /> Free forever plan</span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="border-t border-line bg-paper" data-testid="landing-footer">
        <div className="mx-auto max-w-[1200px] px-6 py-14">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2.5">
                <img src="/logo192.png" alt="Flowlancer" className="w-8 h-8 rounded-md" />
                <span className="font-display text-subhead text-ink">FlowLancer</span>
              </div>
              <p className="mt-4 max-w-[280px] text-small text-ink-muted">
                Proposals, invoices, and payment-gated delivery for independent freelancers.
              </p>
              <div className="mt-5 flex items-center gap-4">
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-ink-subtle hover:text-forest transition-colors">
                  <Twitter className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <a href="https://github.com" target="_blank" rel="noopener noreferrer" aria-label="GitHub" className="text-ink-subtle hover:text-forest transition-colors">
                  <Github className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="text-ink-subtle hover:text-forest transition-colors">
                  <Linkedin className="h-4 w-4" strokeWidth={1.5} />
                </a>
              </div>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-micro uppercase text-ink-subtle">Product</h4>
              <ul className="mt-4 space-y-2.5">
                {['Features', 'Pricing', 'FAQ'].map((item) => (
                  <li key={item}>
                    <button onClick={() => scrollTo(`#${item.toLowerCase()}`)} className="text-small text-ink-muted hover:text-forest transition-colors">{item}</button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-micro uppercase text-ink-subtle">Company</h4>
              <ul className="mt-4 space-y-2.5">
                {['About', 'Blog', 'Careers', 'Contact'].map((item) => (
                  <li key={item}><a href="#" className="text-small text-ink-muted hover:text-forest transition-colors">{item}</a></li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-micro uppercase text-ink-subtle">Legal</h4>
              <ul className="mt-4 space-y-2.5">
                {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
                  <li key={item}><a href="#" className="text-small text-ink-muted hover:text-forest transition-colors">{item}</a></li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 flex flex-col gap-3 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-small text-ink-subtle">&copy; {new Date().getFullYear()} FlowLancer</p>
            <a href="mailto:support@flowlancer.com" className="flex items-center gap-1.5 text-small text-ink-subtle hover:text-forest transition-colors">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> support@flowlancer.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
